import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Settings, Eye, Save, Shield, FileText, Loader2, Crown } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getSiteSettings, saveSiteSettings } from '../services/api';
import { THRONE_DEFAULTS } from '../components/ThroneLinkCard';

const SiteSettingsPage = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const defaultSettings = {
    hero: {
      badge: 'Fictional Crime Investigation Platform',
      headline: "DEADLINE by Daria",
      description: "Dive deep into exclusive fictional crime investigations. Uncover case files, evidence, and behind-the-scenes analysis not available anywhere else. Join fellow detectives in solving the mysteries.",
      bannerImage: 'https://customer-assets.emergentagent.com/job_membership-core/artifacts/7p1ryw8r_household%20mishaps%20%28394%29.JPG',
      ctaButtonText: 'Start Investigation',
      secondaryButtonText: 'Sign In'
    },
    tiers: {
      sectionTitle: 'Case Access Levels',
      sectionDescription: 'Choose your investigative clearance. Higher tiers unlock deeper case files and exclusive evidence.'
    },
    features: {
      sectionTitle: 'Inside the Detective Bureau',
      sectionDescription: 'Fictional detective-themed photosets and murder mystery thriller films',
      feature1Title: 'Exclusive Case Files',
      feature1Description: 'Access detailed fictional case files, staged crime scene photosets, and thriller films',
      feature2Title: 'Direct Expert Access',
      feature2Description: 'Connect directly with investigative insights and expert commentary',
      feature3Title: 'Detective Community',
      feature3Description: 'Join discussions with fellow investigators and collaborate on theories'
    },
    cta: {
      title: 'Ready to Join the Investigation?',
      description: 'Start with basic access and upgrade anytime for deeper case penetration.',
      buttonText: 'Begin Your Investigation'
    },
    branding: {
      siteName: "DEADLINE by Daria",
      primaryColor: '#DC2626',
      tagline: 'Unravel the Mysteries.'
    },
    throneLink: { ...THRONE_DEFAULTS }
  };

  const [settings, setSettings] = useState(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSiteSettings();
      setSettings({...defaultSettings, ...data});
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load settings. Using defaults.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOwner) {
      loadSettings();
    }
  }, [isOwner, loadSettings]);

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-600 mb-4" />
          <h1 className="text-2xl font-headline text-amber-500 mb-4 tracking-wider">ACCESS DENIED</h1>
          <p className="text-gray-400 font-typewriter mb-6">This area is restricted to the platform owner.</p>
          <Button onClick={() => navigate('/feed')} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
            Back to Evidence Files
          </Button>
        </div>
      </div>
    );
  }

  const updateField = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveSiteSettings(settings);
      setHasChanges(false);
      toast({
        title: 'Settings Saved',
        description: 'Your site settings have been saved to the database.'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setSaving(true);
      await saveSiteSettings(settings);
      setHasChanges(false);
      
      const previewWindow = window.open('/', '_blank');
      if (!previewWindow) {
        toast({
          title: 'Popup Blocked',
          description: 'Please allow popups or click "View Live Site" button below.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Preview Opened',
          description: 'Homepage opened in new tab with your changes.'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings before preview.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewLive = async () => {
    try {
      setSaving(true);
      await saveSiteSettings(settings);
      setHasChanges(false);
      window.location.href = '/';
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save settings.',
        variant: 'destructive'
      });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto text-amber-500 animate-spin mb-4" />
          <p className="text-gray-400 font-typewriter">Loading site configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative" data-testid="site-settings-page">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-6xl relative z-10">
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-block mb-4">
              <div className="bg-amber-900/20 border-2 border-amber-600 px-6 py-2 inline-flex items-center gap-3">
                <Settings className="w-6 h-6 text-amber-500" />
                <h1 className="text-3xl font-headline text-amber-500 tracking-wider" data-testid="settings-title">SITE CONFIGURATION</h1>
              </div>
            </div>
            <p className="text-gray-400 font-typewriter">Customize your landing page content and branding</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={handleViewLive} disabled={saving} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider">
              <Eye className="w-4 h-4 mr-2" />
              View Live Site
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={saving} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider">
              Preview (New Tab)
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || saving} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider disabled:opacity-50">
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-gray-900/80 border border-amber-900/30">
            <TabsTrigger value="hero" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">Hero Section</TabsTrigger>
            <TabsTrigger value="tiers" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">Tiers Section</TabsTrigger>
            <TabsTrigger value="features" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">Features</TabsTrigger>
            <TabsTrigger value="cta" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">Call to Action</TabsTrigger>
            <TabsTrigger value="links" data-testid="links-tab" className="font-headline tracking-wide data-[state=active]:bg-amber-900/50 data-[state=active]:text-amber-500">Links</TabsTrigger>
          </TabsList>

          {/* Hero Section */}
          <TabsContent value="hero" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">HERO SECTION</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Main banner area at the top of your landing page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bannerImage" className="font-headline text-amber-500 tracking-wide">Banner Image URL</Label>
                  <Input
                    id="bannerImage"
                    value={settings.hero.bannerImage}
                    onChange={(e) => updateField('hero', 'bannerImage', e.target.value)}
                    placeholder="https://..."
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                  <p className="text-xs text-gray-500 font-typewriter">The background image for your hero section</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="badge" className="font-headline text-amber-500 tracking-wide">Badge Text</Label>
                  <Input
                    id="badge"
                    value={settings.hero.badge}
                    onChange={(e) => updateField('hero', 'badge', e.target.value)}
                    placeholder="Fictional Crime Investigation Platform"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="headline" className="font-headline text-amber-500 tracking-wide">Main Headline</Label>
                  <Input
                    id="headline"
                    value={settings.hero.headline}
                    onChange={(e) => updateField('hero', 'headline', e.target.value)}
                    placeholder="DEADLINE by Daria"
                    className="text-2xl font-bold bg-gray-800 border-amber-900/50 focus:border-amber-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="font-headline text-amber-500 tracking-wide">Description</Label>
                  <Textarea
                    id="description"
                    value={settings.hero.description}
                    onChange={(e) => updateField('hero', 'description', e.target.value)}
                    placeholder="Describe your platform..."
                    rows={4}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ctaButton" className="font-headline text-amber-500 tracking-wide">Primary Button Text</Label>
                    <Input
                      id="ctaButton"
                      value={settings.hero.ctaButtonText}
                      onChange={(e) => updateField('hero', 'ctaButtonText', e.target.value)}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secondaryButton" className="font-headline text-amber-500 tracking-wide">Secondary Button Text</Label>
                    <Input
                      id="secondaryButton"
                      value={settings.hero.secondaryButtonText}
                      onChange={(e) => updateField('hero', 'secondaryButtonText', e.target.value)}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tiers Section */}
          <TabsContent value="tiers" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">TIERS SECTION</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Customize the pricing/tiers section headers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tiersTitle" className="font-headline text-amber-500 tracking-wide">Section Title</Label>
                  <Input
                    id="tiersTitle"
                    value={settings.tiers.sectionTitle}
                    onChange={(e) => updateField('tiers', 'sectionTitle', e.target.value)}
                    placeholder="Case Access Levels"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tiersDescription" className="font-headline text-amber-500 tracking-wide">Section Description</Label>
                  <Textarea
                    id="tiersDescription"
                    value={settings.tiers.sectionDescription}
                    onChange={(e) => updateField('tiers', 'sectionDescription', e.target.value)}
                    rows={2}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="mt-4 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                  <p className="text-sm text-amber-500/70 font-typewriter">
                    <FileText className="w-4 h-4 inline mr-2" />
                    <strong>Tip:</strong> Individual tier details (prices, benefits) are managed in the "Clearance Levels" tab of the main Command Center.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Section */}
          <TabsContent value="features" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">FEATURES SECTION</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">The three features/benefits displayed on your landing page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="featuresTitle" className="font-headline text-amber-500 tracking-wide">Section Title</Label>
                  <Input
                    id="featuresTitle"
                    value={settings.features.sectionTitle}
                    onChange={(e) => updateField('features', 'sectionTitle', e.target.value)}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="featuresDescription" className="font-headline text-amber-500 tracking-wide">Section Description</Label>
                  <Input
                    id="featuresDescription"
                    value={settings.features.sectionDescription}
                    onChange={(e) => updateField('features', 'sectionDescription', e.target.value)}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="border-t border-amber-900/30 pt-4 space-y-4">
                  <h4 className="font-headline text-amber-500 tracking-wider">FEATURE 1</h4>
                  <div className="space-y-2">
                    <Label htmlFor="feature1Title" className="font-typewriter text-gray-300">Title</Label>
                    <Input
                      id="feature1Title"
                      value={settings.features.feature1Title}
                      onChange={(e) => updateField('features', 'feature1Title', e.target.value)}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feature1Description" className="font-typewriter text-gray-300">Description</Label>
                    <Textarea
                      id="feature1Description"
                      value={settings.features.feature1Description}
                      onChange={(e) => updateField('features', 'feature1Description', e.target.value)}
                      rows={2}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                </div>

                <div className="border-t border-amber-900/30 pt-4 space-y-4">
                  <h4 className="font-headline text-amber-500 tracking-wider">FEATURE 2</h4>
                  <div className="space-y-2">
                    <Label htmlFor="feature2Title" className="font-typewriter text-gray-300">Title</Label>
                    <Input
                      id="feature2Title"
                      value={settings.features.feature2Title}
                      onChange={(e) => updateField('features', 'feature2Title', e.target.value)}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feature2Description" className="font-typewriter text-gray-300">Description</Label>
                    <Textarea
                      id="feature2Description"
                      value={settings.features.feature2Description}
                      onChange={(e) => updateField('features', 'feature2Description', e.target.value)}
                      rows={2}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                </div>

                <div className="border-t border-amber-900/30 pt-4 space-y-4">
                  <h4 className="font-headline text-amber-500 tracking-wider">FEATURE 3</h4>
                  <div className="space-y-2">
                    <Label htmlFor="feature3Title" className="font-typewriter text-gray-300">Title</Label>
                    <Input
                      id="feature3Title"
                      value={settings.features.feature3Title}
                      onChange={(e) => updateField('features', 'feature3Title', e.target.value)}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feature3Description" className="font-typewriter text-gray-300">Description</Label>
                    <Textarea
                      id="feature3Description"
                      value={settings.features.feature3Description}
                      onChange={(e) => updateField('features', 'feature3Description', e.target.value)}
                      rows={2}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CTA Section */}
          <TabsContent value="cta" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider">FINAL CALL-TO-ACTION</CardTitle>
                <CardDescription className="font-typewriter text-gray-400">The bottom CTA section encouraging signups</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ctaTitle" className="font-headline text-amber-500 tracking-wide">Title</Label>
                  <Input
                    id="ctaTitle"
                    value={settings.cta.title}
                    onChange={(e) => updateField('cta', 'title', e.target.value)}
                    placeholder="Ready to Join the Investigation?"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ctaDescription" className="font-headline text-amber-500 tracking-wide">Description</Label>
                  <Textarea
                    id="ctaDescription"
                    value={settings.cta.description}
                    onChange={(e) => updateField('cta', 'description', e.target.value)}
                    rows={2}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ctaButton" className="font-headline text-amber-500 tracking-wide">Button Text</Label>
                  <Input
                    id="ctaButton"
                    value={settings.cta.buttonText}
                    onChange={(e) => updateField('cta', 'buttonText', e.target.value)}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Links Section */}
          <TabsContent value="links" className="space-y-4">
            <Card className="bg-gray-900/90 border-2 border-amber-900/30">
              <CardHeader>
                <CardTitle className="font-headline text-xl text-amber-500 tracking-wider flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-400" />
                  THRONE WISHLIST LINK
                </CardTitle>
                <CardDescription className="font-typewriter text-gray-400">Displayed in the site header, next to the Deadline logo. Leave the URL empty until your Throne page is ready — the logo will show but won't be clickable.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-800/60 border border-amber-900/30 rounded-md">
                  <div>
                    <Label className="font-headline text-amber-500 tracking-wide">Show in Header</Label>
                    <p className="text-xs text-gray-500 font-typewriter">Toggle the Throne logo on or off</p>
                  </div>
                  <Switch
                    checked={settings.throneLink.enabled}
                    onCheckedChange={(v) => updateField('throneLink', 'enabled', v)}
                    data-testid="throne-enabled-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="throneUrl" className="font-headline text-amber-500 tracking-wide">Throne URL</Label>
                  <Input
                    id="throneUrl"
                    value={settings.throneLink.url}
                    onChange={(e) => updateField('throneLink', 'url', e.target.value)}
                    placeholder="https://throne.com/yourname"
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    data-testid="throne-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="throneTitle" className="font-headline text-amber-500 tracking-wide">Title</Label>
                  <Input
                    id="throneTitle"
                    value={settings.throneLink.title}
                    onChange={(e) => updateField('throneLink', 'title', e.target.value)}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    data-testid="throne-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="throneBlurb" className="font-headline text-amber-500 tracking-wide">Blurb</Label>
                  <Textarea
                    id="throneBlurb"
                    value={settings.throneLink.blurb}
                    onChange={(e) => updateField('throneLink', 'blurb', e.target.value)}
                    rows={2}
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    data-testid="throne-blurb-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="throneLogo" className="font-headline text-amber-500 tracking-wide">Logo / Thumbnail Image URL</Label>
                  <Input
                    id="throneLogo"
                    value={settings.throneLink.logoImage}
                    onChange={(e) => updateField('throneLink', 'logoImage', e.target.value)}
                    placeholder="https://..."
                    className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    data-testid="throne-logo-input"
                  />
                </div>

                {/* Live preview */}
                <div className="pt-2">
                  <p className="text-xs text-gray-500 font-typewriter mb-2">Preview:</p>
                  <div className="flex items-center gap-4 bg-gray-950 border border-purple-900/50 rounded-md px-4 py-3" data-testid="throne-admin-preview">
                    <img src={settings.throneLink.logoImage} alt="Throne" className="h-9 w-auto max-w-[150px] object-contain" />
                    <div>
                      {settings.throneLink.title && <span className="font-headline text-purple-300 tracking-wider text-sm">{settings.throneLink.title}</span>}
                      <p className="text-gray-400 font-typewriter text-xs sm:text-sm">{settings.throneLink.blurb}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SiteSettingsPage;
